<template>
  <div>
    <refonte-modal
      v-bind:show="props.show"
      class="!max-w-[1200px]"
      v-on:close="openModalAlertClose">
      <template v-slot:modalHeader>
        <h1>{{ formation.titre }}</h1>
        <div class="item-file-content-title-text-bottom">
          Test de validation des acquis
        </div>
      </template>

      <template v-slot:modalBody>
        <template v-if="loading">
          <spinner
            sizes="w-20 h-20"
            thickness="border-8" />
        </template>
        <template v-else>
          <div
            v-if="result"
            class="p-4">
            <message-box
              source="Votre attestation de fin de formation sera disponible pour téléchargement dès que celle-ci aura été générée par l'organisateur de la session."
              border_color="w-full border-yellow-500"
              font_style="font-bold" />

            <div class="flex">
              <hr class="mx-auto mt-4 w-full max-w-md border-core-300" />
            </div>

            <template v-if="result && result.reussi && result.texteSiAcquis">
              <div class="my-4 text-2xl font-bold">Résultat</div>
              <message-box
                v-bind:source="result.texteSiAcquis"
                border_color="w-full border-jinius-green"
                font_style="text-lg" />
            </template>

            <template
              v-if="result && !result.reussi && result.texteSiNonAcquis">
              <div class="my-4 text-2xl font-bold">Résultat</div>
              <message-box
                v-bind:source="result.texteSiNonAcquis"
                border_color="w-full border-jinius-red"
                font_style="text-lg" />
              <message-box
                v-html="
                  `Vous avez encore la possibilité de refaire le test ${result.nbreTentatives - (result.nbreTentativeSupplementaire ? result.nbreTentativeSupplementaire : 0) - 1} fois.`
                "
                v-if="
                  result.nbreTentatives &&
                  (!result.nbreTentativeSupplementaire ||
                    result.nbreTentativeSupplementaire + 1 <
                      result.nbreTentatives)
                "
                border_color="w-full border-yellow-500"
                font_style="font-bold" />
            </template>

            <template
              v-if="
                (result.nbreBonnesReponses ||
                  result.nbreBonnesReponses === 0) &&
                result.nbreQuestions
              ">
              <div class="my-4 text-2xl font-bold">Note</div>
              <message-box
                v-bind:source="`${result.nbreBonnesReponses}/${result.nbreQuestions}`"
                v-bind:border_color="
                  result.reussi
                    ? 'w-full border-jinius-green'
                    : 'w-full border-jinius-red'
                "
                font_style="text-lg tracking-wider font-bold" />
            </template>

            <div v-if="reponse && reponse.reponsesJson">
              <div class="my-4 text-2xl font-bold">Corrigé</div>

              <surveyjs-wrapper
                v-bind:json="reponse.survey"
                v-bind:data="reponse.reponsesJson" />
            </div>
          </div>

          <template v-else>
            <surveyjs-wrapper
              ref="surveyWrapper"
              v-on:complete="complete"
              v-on:scroll_action="scrollModalToTop"
              v-bind:json="surveyText" />
          </template>
        </template>
      </template>

      <template v-slot:modalFooter>
        <div
          v-if="surveyCatcher"
          class="inline-flex w-full justify-end space-x-4 text-lg">
          <refonte-button
            v-if="!surveyCatcher.isFirstPage"
            v-bind:on_click="() => surveyWrapper.prevPage()">
            Précédent
          </refonte-button>
          <refonte-button
            v-if="!surveyCatcher.isLastPage"
            v-bind:on_click="() => surveyWrapper.nextPage()">
            Suivant
          </refonte-button>
          <refonte-button
            v-if="surveyCatcher.isLastPage && !completed"
            v-bind:on_click="() => surveyWrapper.completeLastPage()">
            Terminer
          </refonte-button>
          <refonte-button
            v-if="completed"
            v-bind:on_click="() => close()">
            Fermer
          </refonte-button>
        </div>
      </template>
    </refonte-modal>
    <satisfaction-alert-modal-close
      v-bind:show="isModalAlertCloseOpen"
      v-bind:on_close="closeModalAlertClose"
      v-bind:on_submit="close" />
  </div>
</template>

<script setup lang="ts">
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import { onMounted, ref, watchEffect } from 'vue';
  import SatisfactionAlertModalClose from '@/views/_espace/_refonte/gestion/_satisfaction/satisfaction-alert-modal-close.vue';
  import { Formation } from '@/types/gestion/commandes-types';
  import { API } from '@/http-common';
  import SurveyjsWrapper from '@/components/utils/surveyjs-wrapper.vue';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import MessageBox from '@/components/utils/message-box.vue';

  const props = withDefaults(
    defineProps<{
      show: boolean;
      on_close: Function;
      formation: Formation;
      on_complete?: Function;
    }>(),
    {}
  );

  const detail = ref(null);
  const loading = ref(false);
  const surveyText = ref({});
  const surveyCatcher = ref(null);
  const surveyWrapper = ref(null);
  const multiple = ref(false);
  const isModalAlertCloseOpen = ref<boolean>(false);
  const result = ref(null);
  const completed = ref<boolean>(false);
  const reponse = ref(null);
  const participantType = ref('');
  const participantId = ref<string | number | null>(null);

  const computeParticipantIdentifiers = () => {
    const typeKeyMap = new Map<number, string>([
      [8, 'cursusInscriptionId'],
      [2, 'elearningParticipantId']
    ]);

    const participantKey =
      typeKeyMap.get(props.formation.type.id) ?? 'sessionParticipantId';

    participantType.value = participantKey;
    participantId.value = props.formation[participantKey] ?? props.formation.id;
  };

  onMounted(() => {
    computeParticipantIdentifiers();
    fetchDetail();
  });

  watchEffect(() => {
    if (surveyWrapper.value) {
      surveyCatcher.value = surveyWrapper.value.survey;
    } else {
      // not mounted yet, or the element was unmounted (e.g. by v-if)
    }
  });

  const close = async () => {
    completed.value = false;
    detail.value = null;
    props.on_close();
  };
  const openModalAlertClose = () => {
    if (completed.value) {
      props.on_close();
    } else {
      isModalAlertCloseOpen.value = true;
    }
  };
  const closeModalAlertClose = () => {
    isModalAlertCloseOpen.value = false;
  };

  const fetchDetail = async () => {
    loading.value = true;

    const response = await API.get(
      `participant/evaluation?parcoursProduitId=${props.formation.produitId}&${participantType.value}=${participantId.value}&typeId=2`
    );
    if (response && response.data) {
      surveyText.value = response.data.survey;
      multiple.value = response.data.multiple;
    }

    loading.value = false;
  };

  const complete = async () => {
    loading.value = true;

    const data = surveyCatcher.value?.data || {};

    const response = await API.post(
      `participant/evaluation${multiple.value ? '/multiple' : ''}?parcoursProduitId=${props.formation.produitId}&${participantType.value}=${participantId.value}&typeId=2`,
      data
    );

    if (response && response.data) {
      result.value = response.data;
    }
    const responseCorrige = await API.get(
      `participant/evaluation/reponse?parcoursProduitId=${props.formation.produitId}&${participantType.value}=${participantId.value}&typeId=2`
    );

    if (responseCorrige && responseCorrige.data) {
      reponse.value = responseCorrige.data;
    }
    if (props.on_complete) {
      props.on_complete();
    }
    completed.value = true;
    loading.value = false;
  };
</script>

<style lang="scss" scoped></style>
